<template>
    <t-resource v-if="!googleDocAddingInProgress"
        :projectUuid="projectUuid"
        :onGetProjects="getProjects"
        :onGetResources="getResources"
        :onGetResourceByKey="getResourceByKey"
        :onGetLanguages="getLanguages"
        :onGetProjectLanguages="getProjectLanguages"
        :onAddResource="addResource"
        :onAddGoogleDoc="addGoogleDoc"
        :onTranslationGoogle="translationGoogle"
        :onEditResource="editResource"
        :onDeleteResource="deleteResource"
        :onGetTags="getTags"
        :onUploadFile="uploadFile"
        :onSearchGoogleDocs="searchGoogleDocs"
        :onSetGoogleDocPermissions="setGoogleDocPermissions"
    />
</template>

<script>
import TResource from '@templates2/t-resource'
import ProjectService from '@services/project-service'
import _ from 'lodash'
import {
    userComputed,
    authComputed,
    resourcesComputed,
    resourcesMethods,
} from '@state/helpers'
import LanguageService from '@services/language-service'
import ResourceService from '@services/resource-service'
import GoogleDocService from '@services/google-doc-service'
import { delay } from '@utils/helpers'
import FileStoreService from '@services/file-store-service'
import { 
    NeedGoogleDocAuthError
} from '@utils/errors'


export default {
    name: 'PResource',
    components: {
        TResource
    },
    props: {
    },
    computed: {
        ...authComputed,
        ...userComputed,
        ...resourcesComputed,
        projectUuid() {
            return this.$route.params.projectUuid
        }
    },
    watch: {
    },
    async created() {
        if (this.$route.query.externalDocId && this.$route.query.code && this.$route.query.state) {
            this.googleDocAddingInProgress = true;
            await GoogleDocService.authGoogleDoc({
                authId: this.$route.query.state,
                authCode: this.$route.query.code
            })
            const parts = this.$route.query.state.split('@');
            if (this.addedGoogleDoc && parts[0] === this.projectUuid && parts[1] === this.addedGoogleDoc.externalDocId) {
                await GoogleDocService.addGoogleDoc({
                    projectUuid: this.projectUuid,
                    data: this.addedGoogleDoc.params
                })
                
                this.googleDocAddingInProgress = false;
                this.$router.replace({
                    name: "project-resources",
                    params: {
                        projectUuid: this.projectUuid
                    },
                    query: {}
                })
            }
        }
    },
    data() {
        return {
            googleDocAddingInProgress: false
        }
    },
    methods: {
        ...resourcesMethods,
        async getProjects(params) {
            if (!this.projects) {
                const { items } = await ProjectService.getProjects({
                    userUuid: this.userUuid,
                    params: {
                        pageSize: 1000,
                    },
                })
                this.projects = items;
            }

            if (params && params.projectUuid) {
                return this.projects.filter(prj => prj.projectUuid == params.projectUuid);
            } else {
                return this.projects;
            }
        },
        async getResources(params) {
            return await ResourceService.getResources({
                projectUuid: this.projectUuid,
                params,
            })
        },
        async getResourceByKey(resourceKey) {
            return await ResourceService.getResourceKey({
                projectUuid: this.projectUuid,
                key: resourceKey,
            });
        },
        async getLanguages() {
            const { items } = await LanguageService.getProjectLanguages({
                projectUuid: this.projectUuid,
                params: {
                    pageSize: 1000,
                },
            })
            return items;
        },
        async addResource(params) {
            await ResourceService.addResources({
                projectUuid: this.projectUuid,
                langCode: params.langCode,
                data: [
                    {
                        key: params.key,
                        value: params.value,
                        description: params.description,
                        tags: params.tags,
                        kind: params.kind,
                    },
                ],
            })
        },
        getExternalDocIdFromUrl(docUrl) {
            //const regexp = /docs\.google\.com\/(document|spreadsheets|presentation)\/d\/(.*)\//i;
            const regexp = /docs\.google\.com\/(document)\/d\/(.*)\//i;
            const match = regexp.exec(docUrl);
            if (!match) {
                throw new Error("WrongUrl");
            }
            return match[2];
        },
        async addGoogleDoc(params) {
            try {
                const externaDocId = this.getExternalDocIdFromUrl(params.url);
                this.setAddedGoogleDoc({
                    externalDocId: externaDocId,
                    params: params
                });
                await GoogleDocService.addGoogleDoc({
                    projectUuid: this.projectUuid,
                    data: params
                })
            } catch(err) {
                if (err instanceof NeedGoogleDocAuthError) {
                    window.location.href = err.detailedInfo.authUrl;
                } else {
                    throw err;
                }
            }
        },
        async translationGoogle(project, resource) {
            try {
                await ResourceService.postTranslationGoogle({
                    projectUuid:
                        project && project.projectUuid
                            ? project.projectUuid
                            : null,
                    resourceKey: resource.key,
                })
            } catch (err) {
                throw err
            }
        },
        async editResource(params) {
            await ResourceService.updateResourceInfo({
                projectUuid: this.projectUuid,
                key: params.key,
                data: {
                    newKey: params.newKey ? params.newKey : undefined,
                    safetyValueChange: params.safetyValueChange ? params.safetyValueChange : undefined,
                    value: params.value,
                    description: params.description,
                    tags: params.tags,
                    primaryLanguage: params.langCode,
                    kind: params.kind,
                },
            })
        },
        async deleteResource(resource) {
            if (resource.kind == "googledoc") {
                await GoogleDocService.deleteGoogleDoc({
                    projectUuid: this.projectUuid,
                    externalId: resource.key,
                })
            } else {
                await ResourceService.deleteResourceKey({
                    projectUuid: this.projectUuid,
                    key: resource.key,
                })
            }
        },
        async getTags() {
            const { items } = await ResourceService.getResourcesTags({
                projectUuid: this.projectUuid,
                params: {
                    pageSize: 1000,
                },
            })
            return items;
        },
        async uploadFile(file) {
            const secureInfo = await ProjectService.getProjectSecureInfo({ projectUuid: this.projectUuid })
            const url = await FileStoreService.uploadFile({ 
                login: secureInfo.fileStoreUser,
                password: secureInfo.fileStorePassword,
                file 
            });
            return url;
        },
        async searchGoogleDocs(params) {
            return await GoogleDocService.searchGoogleDocs({
                ...params,
                projectUuid: this.projectUuid,
            })
        },
        async setGoogleDocPermissions(params) {
            return await GoogleDocService.addGooglePermissions({
                projectUuid: this.projectUuid,
                data: params
            })
        },
        async getProjectLanguages() {
            return await LanguageService.getProjectLanguages({
                projectUuid: this.projectUuid,
                params: {
                    pageSize: 1000,
                },
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@import 'theme';
</style>
